import { FC } from 'react';
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { UserWithProfile } from '@/integrations/supabase/user-types';
import { Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface UserActionsProps {
  user: UserWithProfile;
  onSuccess: () => void;
}

export const UserActions: FC<UserActionsProps> = ({ user, onSuccess }) => {
  const handleUpdateRole = async (userId: string, newRole: 'admin' | 'user') => {
    try {
      const { data, error } = await supabase
        .rpc('update_user_role', {
          user_id: userId,
          new_role: newRole
        });

      if (error) throw error;

      toast.success("Rol de usuario actualizado correctamente");
      onSuccess();
    } catch (error: any) {
      console.error('Error updating user role:', error);
      toast.error(error.message || "No se pudo actualizar el rol del usuario");
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);

      if (error) throw error;

      toast.success("Usuario eliminado correctamente");
      onSuccess();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error("No se pudo eliminar el usuario");
    }
  };

  const currentRole = user.role || 'user';
  const userName = user.tutor_name || 'este usuario';

  return (
    <div className="flex flex-wrap gap-2 justify-end">
      <Button 
        variant="outline" 
        size="sm"
        className="flex-shrink-0 w-auto"
        onClick={() => handleUpdateRole(
          user.id, 
          currentRole === 'admin' ? 'user' : 'admin'
        )}
      >
        Cambiar a {currentRole === 'admin' ? 'Usuario' : 'Admin'}
      </Button>
      
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button 
            variant="destructive" 
            size="sm"
            className="flex-shrink-0 hover:bg-red-600 transition-colors duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            <span>Eliminar</span>
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente la cuenta
              de {userName} y todos sus datos asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteUser(user.id)}
              className="bg-red-600 hover:bg-red-700 text-white transition-colors duration-200"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};